import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, type AuditEntry, type University } from "../api.js";
import { useAuth } from "../auth.js";

/** Actions worth a second look when reading the trail. */
const NOTABLE = ["UNIVERSITY_SUSPEND", "UNIVERSITY_UPDATE", "USER_DEACTIVATE"];

/** Privileged-action trail: who created, appointed, suspended or deactivated what. */
export default function Audit() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  // A super admin can narrow the global trail to one institution; everyone
  // else only ever sees their own, filter or not.
  const [params, setParams] = useSearchParams();
  const universityId = params.get("universityId") ?? "";

  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [universities, setUniversities] = useState<University[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    api
      .get<{ entries: AuditEntry[] }>(
        `/api/admin/audit${universityId ? `?universityId=${universityId}` : ""}`
      )
      .then((r) => setEntries(r.entries))
      .catch((e) => setError(e.message));
  }, [universityId]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    api
      .get<{ universities: University[] }>("/api/admin/universities")
      .then((r) => setUniversities(r.universities))
      .catch(() => {});
  }, [isSuperAdmin]);

  return (
    <div className="page">
      <div className="between" style={{ marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Audit log</h1>
          <div className="muted">Privileged actions, most recent first</div>
        </div>
        <div className="row">
          {isSuperAdmin && universities.length > 0 && (
            <select
              style={{ width: "auto" }}
              value={universityId}
              onChange={(e) => {
                const v = e.target.value;
                setParams(v ? { universityId: v } : {}, { replace: true });
              }}
            >
              <option value="">All institutions</option>
              {universities.map((u) => (
                <option key={u.id} value={u.id}>{u.code} — {u.name}</option>
              ))}
            </select>
          )}
          <Link to={isSuperAdmin ? "/institutions" : "/"}><button>Back</button></Link>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="card">
        {entries.length === 0 ? (
          <div className="muted">Nothing recorded yet.</div>
        ) : (
          <table>
            <thead>
              <tr><th>When</th><th>Who</th><th>Action</th><th>Target</th><th>Details</th><th>IP</th></tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id}>
                  <td className="muted">{new Date(e.createdAt).toLocaleString()}</td>
                  <td>
                    {e.actor.name}
                    <div className="muted" style={{ fontSize: 12 }}>{e.actor.email}</div>
                  </td>
                  <td>
                    <span className={`badge ${NOTABLE.includes(e.action) ? "warn" : "draft"}`}>
                      {e.action.toLowerCase().replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="muted">{e.targetType}</td>
                  <td className="muted">{e.details}</td>
                  <td className="muted">{e.ip}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
